<template>
  <v-row align="center">
    <!-- Global only Selector -->
    <v-col
      v-if="shouldShowGlobal && showGlobalOption"
      cols="12"
      md="5"
    >
      <v-checkbox
        v-model="globalTeamsOnly"
        class="pa-0 ma-0"
        label="Accessible by Global Team Only"
        :persistent-hint="true"
        hint="Check this if the case should be accessible only by members of the Global Team"
        @change="onGlobalOnlyChange"
      />
    </v-col>

    <v-col
      v-if="shouldShowGlobal && showGlobalOption"
      cols="1"
    >
      <v-row
        align="center"
        no-gutters
        class="my-2"
      >
        <v-divider />
        <span class="mx-4 text-caption text-uppercase"> OR </span>
        <v-divider />
      </v-row>
    </v-col>
    <!-- Team Selector -->
    <v-col>
      <v-autocomplete
        v-model="localTeamIds"
        chips
        clearable
        deletable-chips
        hide-selected
        multiple
        outlined
        persistent-hint
        :disabled="isSelectDisabled || globalTeamsOnly"
        :hint="teamsHint"
        :items="teams"
        :label="label"
        :loading="teamsLoading"
        :rules="finalRules"
        @change="onTeamsChange"
        @blur="emitState"
      />
    </v-col>

    <!-- Validation Error -->
    <v-col cols="12">
      <v-row
        v-if="isGlobalTeamOnlyDisabled"
      >
        <v-col
          cols="12"
        >
          <v-alert
            dense
            outlined
            type="error"
          >
            You cannot select teams and also enable Global Teams Only.
          </v-alert>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { SHARED_NAMESPACE } from '@/store/shared'
import { USER_NAMESPACE } from '@/store/user'

export default {
  name: 'SelectTeams',

  model: {
    prop: 'teamIds',
    event: 'change',
  },

  props: {
    hint: {
      type: String,
      default: '',
    },

    label: {
      type: String,
      default: 'Team Selection',
    },

    recordId: {
      type: String,
      default: null,
    },

    resetTriggered: {
      type: Boolean,
      default: false,
    },

    showGlobalOption: {
      type: Boolean,
      default: true,
    },

    teamIds: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['change', 'selected', 'validation'],

  data () {
    return {
      globalTeamsOnly: false,
      localTeamIds: [
        ...this.teamIds,
      ],
    }
  },

  computed: {
    finalRules () {
      return [
        () => this.isValid || 'Select teams or enable Global Teams Only.',
      ]
    },

    isGlobalTeamOnlyDisabled () {
      return this.globalTeamsOnly && this.localTeamIds.length > 0
    },

    isSelectDisabled () {
      if (this.shouldShowGlobal) return false

      return !this.isUserScopeGlobal
    },

    isUserScopeGlobal () {
      return this.currentUser.scope_is_global
    },

    isValid () {
      if (this.showConflictError) return false
      if (this.globalTeamsOnly && this.localTeamIds.length === 0) return true
      if (!this.globalTeamsOnly && this.localTeamIds.length > 0) return true
      return false
    },

    shouldShowGlobal () {
      return this.shouldBeTreatedAsGlobal
    },

    showConflictError () {
      return this.globalTeamsOnly && this.localTeamIds.length > 0
    },

    teams () {
      return this.getCachedDropdown(`my_teams_${this.recordId || 'none'}`)
    },

    teamsHint () {
      if (this.hint) return this.hint
      if (this.shouldShowGlobal) return 'What teams would you like to have access to this record other than the Global Team? Global Team is added by default'
      if (this.isUserScopeGlobal) return 'What teams would you like to have access to this record?'

      return 'You can only select your active team'
    },

    teamsLoading () {
      return this.isCachedDropdownLoading(`my_teams_${this.recordId || 'none'}`)
    },

    ...mapGetters([
      'currentUser',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
      'isCachedDropdownLoading',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'shouldBeTreatedAsGlobal',
    ]),
  },

  watch: {
    resetTriggered: {
      deep: true,

      handler (val) {
        if (val) this.reset()
      },

      immediate: true,
    },
  },

  created () {
    this.fetchOptions()
    this.updateSelectedTeams()
  },

  methods: {
    emitState () {
      const state = {
        globalTeamsOnly: this.globalTeamsOnly,
        isValid: this.isValid,
        teamIds: this.localTeamIds,
      }

      this.$emit('change', this.localTeamIds)
      this.$emit('selected', state)
      this.$emit('validation', state.isValid)
    },

    fetchOptions () {
      const params = { user_id: this.currentUser.id }
      if (this.recordId) params.record_id = this.recordId

      this.getDropdownOptions({
        force: true,
        identifier: `my_teams_${this.recordId || 'none'}`,
        path: 'teams/my_teams',
        queryParams: params,
      })
    },

    onGlobalOnlyChange () {
      this.emitState()
    },

    onTeamsChange () {
      this.emitState()
    },

    reset () {
      this.globalTeamsOnly = false
      this.localTeamIds = []
      this.emitState()
    },

    updateSelectedTeams () {
      if (this.shouldBeTreatedAsGlobal) return

      this.localTeamIds = [this.currentUser.active_team_id]
      this.emitState()
    },

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
